import type { ReportingEntry } from "@/shared/cache/slices/reportingEntries";
import { getReportingEntryStatus } from "../hooks/reportingStatus";

export type ReportingExportFormat = "csv" | "pdf";

export type ReportingExport = {
	entries: ReportingEntry[];
	from: string;
	to: string;
	scope: string;
	status: string;
	client: string;
	matter: string;
};

export function reportingExportFilename(
	data: ReportingExport,
	format: ReportingExportFormat,
) {
	return `reporting_entries_${data.from}_to_${data.to}.${format}`;
}

export function formatEntryDate(date: string | null | undefined): string {
	return date?.split("T")[0] ?? "";
}

export function getEntryStatus(entry: ReportingEntry): string {
	return getReportingEntryStatus(entry);
}

// Freeze the flat reporting records before any async PDF imports or font loads.
export function snapshotReportingExport(
	data: ReportingExport,
): ReportingExport {
	return {
		...data,
		entries: data.entries
			.map((entry) => ({ ...entry }))
			.sort(
				(a, b) =>
					formatEntryDate(a.date).localeCompare(formatEntryDate(b.date)) ||
					(a.prof_name || "").localeCompare(b.prof_name || "") ||
					(a.start_time || "").localeCompare(b.start_time || "") ||
					a.id - b.id,
			),
	};
}

// Sum the same hundredths displayed on each row, avoiding floating point drift.
export function reportingExportTotals(entries: ReportingEntry[]) {
	let total = 0;
	let exported = 0;
	let deleted = 0;
	for (const entry of entries) {
		const hundredths = Math.round(entry.units * 100);
		total += hundredths;
		const status = getEntryStatus(entry);
		if (status === "exported") exported += hundredths;
		else if (status === "deleted") deleted += hundredths;
	}
	return {
		total: total / 100,
		exported: exported / 100,
		tracked: (total - exported - deleted) / 100,
		deleted: deleted / 100,
	};
}

type CsvCell = boolean | number | string | null | undefined;
const CSV_COLUMNS: {
	header: string;
	value: (entry: ReportingEntry) => CsvCell;
}[] = [
	{ header: "Transaction ID", value: (entry) => entry.id },
	{ header: "Professional ID", value: (entry) => entry.prof_id },
	{ header: "Attorney", value: (entry) => entry.prof_name },
	{ header: "Status", value: getEntryStatus },
	{ header: "Date", value: (entry) => formatEntryDate(entry.date) },
	{ header: "Start Time", value: (entry) => entry.start_time },
	{ header: "End Time", value: (entry) => entry.end_time },
	{ header: "Hours", value: (entry) => Math.round(entry.units * 100) / 100 },
	{ header: "Client", value: (entry) => entry.client },
	{ header: "Matter Number", value: (entry) => entry.matter_number },
	{ header: "Matter Description", value: (entry) => entry.matter_description },
	{ header: "Task", value: (entry) => entry.task },
	{ header: "Activity", value: (entry) => entry.activity },
	{ header: "Application", value: (entry) => entry.application },
	{ header: "Description", value: (entry) => entry.description },
];

function escapeCsvCell(value: CsvCell): string {
	if (value === null || value === undefined) return "";
	const str = String(value);
	if (/[",\r\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
	return str;
}

export function buildReportingEntriesCsv(entries: ReportingEntry[]): string {
	return [
		CSV_COLUMNS.map((column) => escapeCsvCell(column.header)).join(","),
		...entries.map((entry) =>
			CSV_COLUMNS.map((column) => escapeCsvCell(column.value(entry))).join(","),
		),
	].join("\n");
}

export function downloadReportingCsv(data: ReportingExport): void {
	const blob = new Blob(["\ufeff", buildReportingEntriesCsv(data.entries)], {
		type: "text/csv;charset=utf-8;",
	});
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = reportingExportFilename(data, "csv");
	document.body.appendChild(link);
	try {
		link.click();
	} finally {
		link.remove();
		// Allow the browser to consume the download before releasing its URL.
		setTimeout(() => URL.revokeObjectURL(url), 1000);
	}
}
